import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fieldDb } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import type { ExtractionStatus } from '@/types';

interface Row {
  id: string;
  due_date: string | null;
  building_name_text: string | null;
  order_number: string | null;
  progress_state: string;
  status: ExtractionStatus;
  partner_id: string | null;
  source_type: string;
}

const PROGRESS_LABEL: Record<string, string> = {
  pending: '未着手',
  in_progress: '作業中',
  completed: '完了',
  sent: '送信済',
  canceled: 'キャンセル',
};

const STATUS_LABEL: Record<ExtractionStatus, string> = {
  draft: '仮',
  confirmed: '確定',
  rolled_back: '巻戻',
  sent_to_provider: '板送信',
  archived: 'アーカイブ',
};

const STATUS_COLOR: Record<ExtractionStatus, string> = {
  draft: 'bg-amber-100 text-amber-800',
  confirmed: 'bg-emerald-100 text-emerald-800',
  rolled_back: 'bg-rose-100 text-rose-800',
  sent_to_provider: 'bg-sky-100 text-sky-800',
  archived: 'bg-slate-100 text-slate-500',
};

type FilterKey = 'draft' | 'active' | 'all';

const FILTER_TABS: { key: FilterKey; label: string }[] = [
  { key: 'draft', label: '仮案件' },
  { key: 'active', label: '稼働中' },
  { key: 'all', label: 'すべて' },
];

export function ExtractionsPage() {
  const { tenantId } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterKey>('draft');

  useEffect(() => {
    if (!tenantId) return;
    (async () => {
      const { data } = await fieldDb()
        .from('extractions')
        .select(
          'id, due_date, building_name_text, order_number, progress_state, status, partner_id, source_type',
        )
        .order('due_date', { ascending: true, nullsFirst: false })
        .limit(200);
      setRows((data ?? []) as Row[]);
      setLoading(false);
    })();
  }, [tenantId]);

  const filtered = useMemo(() => {
    switch (filter) {
      case 'draft':
        return rows.filter((r) => r.status === 'draft');
      case 'active':
        return rows.filter((r) => r.status !== 'archived' && r.status !== 'rolled_back');
      case 'all':
      default:
        return rows;
    }
  }, [rows, filter]);

  const counts = useMemo(
    () => ({
      draft: rows.filter((r) => r.status === 'draft').length,
      active: rows.filter((r) => r.status !== 'archived' && r.status !== 'rolled_back').length,
      all: rows.length,
    }),
    [rows],
  );

  return (
    <div>
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <h1 className="text-sm font-semibold">案件</h1>
        <div className="mt-2 flex gap-1">
          {FILTER_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setFilter(t.key)}
              className={`rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                filter === t.key
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {t.label} <span className="ml-0.5 opacity-70">{counts[t.key]}</span>
            </button>
          ))}
        </div>
      </header>
      {loading ? (
        <div className="p-6 text-sm text-slate-500">読み込み中...</div>
      ) : filtered.length === 0 ? (
        <div className="p-6 text-sm text-slate-500">
          {filter === 'draft'
            ? '仮案件はありません。LINE / メール / 手動アップロードから取り込まれた PDF が、ここに表示されます。'
            : '案件はありません。'}
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {filtered.map((r) => (
            <li key={r.id}>
              <Link to={`/extractions/${r.id}`} className="block px-4 py-3 hover:bg-slate-50">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="flex min-w-0 flex-1 items-baseline gap-2">
                    <span
                      className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-semibold ${STATUS_COLOR[r.status]}`}
                    >
                      {STATUS_LABEL[r.status]}
                    </span>
                    <div className="truncate text-sm font-medium">
                      {r.building_name_text ?? '(無題)'}
                    </div>
                  </div>
                  <div className="shrink-0 text-[10px] text-slate-500">
                    {r.due_date ?? '期日未設定'}
                  </div>
                </div>
                <div className="mt-0.5 flex items-baseline gap-2 text-[10px] text-slate-500">
                  {r.order_number && <span>#{r.order_number}</span>}
                  <span>{PROGRESS_LABEL[r.progress_state] ?? r.progress_state}</span>
                  <span className="uppercase">{r.source_type}</span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
